import {
  Users,
  Download,
  Eye,
  Settings,
  Lightbulb,
  Crown,
  Shield,
  HelpCircle,
  UserCircle,
  Megaphone,
  FileText,
  ShoppingCart,
  Mail,
  Bell,
  Settings2,
  CalendarClock,
  MessageSquare,
  Link2,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'
import { CATEGORICAL } from '@/lib/theme'

export default function AdministrationSection() {
  return (
    <>
      <section id="users" aria-labelledby="users-heading">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-4 w-4" aria-hidden="true" />
          <h3 id="users-heading" className="font-semibold">
            User Management
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Administrators can create, edit, and manage user accounts. The system supports seven user
          roles:
        </p>
        <div className="space-y-2 ml-2 mb-3">
          <div className="flex items-start gap-2">
            <Crown
              className="h-4 w-4 mt-0.5 flex-shrink-0"
              style={{ color: CATEGORICAL.prussian.solid }}
              aria-hidden="true"
            />
            <div>
              <span className="font-medium text-sm">Super Admin</span>
              <span className="text-sm text-muted-foreground">
                {' '}
                — Full system control. Can manage all users including other administrators, access
                all features, and export the entire CRM database.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Shield
              className="h-4 w-4 mt-0.5 flex-shrink-0"
              style={{ color: CATEGORICAL.plum.solid }}
              aria-hidden="true"
            />
            <div>
              <span className="font-medium text-sm">Admin</span>
              <span className="text-sm text-muted-foreground">
                {' '}
                — User management privileges. Can create and edit regular users, export data, send
                daily reports, and impersonate non-admin users for troubleshooting.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <UserCircle
              className="h-4 w-4 mt-0.5 flex-shrink-0"
              style={{ color: CATEGORICAL.navy.solid }}
              aria-hidden="true"
            />
            <div>
              <span className="font-medium text-sm">Sales (Standard)</span>
              <span className="text-sm text-muted-foreground">
                {' '}
                — Full CRM access. Can view and manage all records, create activities, and run
                reports. This is the default role for sales team members.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Megaphone
              className="h-4 w-4 mt-0.5 flex-shrink-0"
              style={{ color: CATEGORICAL.evergreen.solid }}
              aria-hidden="true"
            />
            <div>
              <span className="font-medium text-sm">Marketing</span>
              <span className="text-sm text-muted-foreground">
                {' '}
                — Focused on lead generation and outreach. Access to Leads, Accounts, Contacts,
                Tasks, Events, Reports, Analytics, Content, and Tickets. Cannot access
                Opportunities, Quotes, Orders, or Products.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <ShoppingCart
              className="h-4 w-4 mt-0.5 flex-shrink-0"
              style={{ color: CATEGORICAL.clay.solid }}
              aria-hidden="true"
            />
            <div>
              <span className="font-medium text-sm">Orders</span>
              <span className="text-sm text-muted-foreground">
                {' '}
                — Focused on order fulfillment. Access to Orders, Products, Accounts, Contacts,
                Tasks, Events, Reports, Analytics, Content, and Tickets. Cannot access Leads,
                Opportunities, or Quotes.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <FileText
              className="h-4 w-4 mt-0.5 flex-shrink-0"
              style={{ color: CATEGORICAL.teal.solid }}
              aria-hidden="true"
            />
            <div>
              <span className="font-medium text-sm">Quotes</span>
              <span className="text-sm text-muted-foreground">
                {' '}
                — Focused on quoting and proposals. Access to Quotes, Opportunities, Products,
                Accounts, Contacts, Tasks, Events, Reports, Analytics, Content, and Tickets. Cannot
                access Leads or Orders.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <HelpCircle
              className="h-4 w-4 mt-0.5 flex-shrink-0"
              style={{ color: CATEGORICAL.slate.solid }}
              aria-hidden="true"
            />
            <div>
              <span className="font-medium text-sm">Support</span>
              <span className="text-sm text-muted-foreground">
                {' '}
                — Ticketing access only. Can view and manage support tickets but cannot access CRM
                data, sales records, or analytics.
              </span>
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Additional actions:</span>
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Export users to CSV for reporting</li>
          <li>Deactivate users to revoke access (soft delete — no data is lost)</li>
          <li>Reactivate previously deactivated users</li>
          <li>Permanently delete inactive users who have no associated records</li>
        </ul>
      </section>
      <Separator />

      <section id="exporting" aria-labelledby="exporting-heading">
        <div className="flex items-center gap-2 mb-2">
          <Download className="h-4 w-4" aria-hidden="true" />
          <h3 id="exporting-heading" className="font-semibold">
            Exporting Data
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Need your data in a spreadsheet? Admin and Super Admin users can export records to CSV:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Export buttons appear on list pages for Accounts, Contacts, Leads, Opportunities, Tasks,
            Events, and Tickets
          </li>
          <li>The export includes all records matching your current filters</li>
          <li>
            <span className="font-medium">Super Admin only:</span> Export All Records in Settings
            downloads a ZIP file with every table in the CRM as separate CSV files. This export is
            generated server-side, so it captures the full dataset regardless of any on-screen
            filters
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Note:</span> Export buttons are hidden when impersonating
          non-admin users to preserve data access controls.
        </p>
      </section>
      <Separator />

      <section id="impersonation" aria-labelledby="impersonation-heading">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="h-4 w-4" aria-hidden="true" />
          <h3 id="impersonation-heading" className="font-semibold">
            User Impersonation
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Impersonation lets administrators view the CRM exactly as another user sees it. This is
          useful for troubleshooting permission issues, verifying a user's setup, or reviewing a
          teammate's pipeline.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">How it works:</span>
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Go to the Users page and click the impersonate icon next to a user's name</li>
          <li>An amber banner appears at the top showing who you are viewing as</li>
          <li>
            The CRM switches to <span className="font-medium">read-only mode</span> — you cannot
            make any changes while impersonating
          </li>
          <li>
            Click <span className="font-medium">Exit Impersonation</span> in the banner to return to
            your own view
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Access rules:</span> Admins can impersonate Sales,
          Marketing, Orders, Quotes, and Support users. Super Admins can also impersonate Admins.
          Nobody can impersonate a Super Admin.
        </p>
      </section>
      <Separator />

      <section id="settings" aria-labelledby="settings-heading">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="h-4 w-4" aria-hidden="true" />
          <h3 id="settings-heading" className="font-semibold">
            Settings
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          The Settings page uses a tabbed layout. Select a tab to configure that area:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Google</span> — Connect your own Google Calendar and Gmail
            for event sync, email tracking, and Google Meet scheduling. See{' '}
            <a
              href="#google-integration"
              className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              details below
            </a>
            .
          </li>
          <li>
            <span className="font-medium">Mailchimp</span> (Admin, Marketing) — Connect your
            Mailchimp account to sync CRM contacts to email audiences. See{' '}
            <a
              href="#mailchimp-integration"
              className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              details below
            </a>
            .
          </li>
          <li>
            <span className="font-medium">Google Chat</span> (Admin) — Send CRM event notifications
            to a Google Chat space via webhook. See{' '}
            <a
              href="#google-chat-settings"
              className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              details below
            </a>
            .
          </li>
          <li>
            <span className="font-medium">Notifications</span> — Real-time alerts for assignments,
            deal updates, and overdue tasks via the bell icon in the header. See{' '}
            <a
              href="#notifications-settings"
              className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              details below
            </a>
            .
          </li>
          <li>
            <span className="font-medium">Data &amp; Privacy</span> (Super Admin) — Download the
            complete CRM database as a ZIP file
          </li>
          <li>
            <span className="font-medium">Reports</span> (Admin) — Preview and send daily executive
            summary reports to admin users
          </li>
          <li>
            <span className="font-medium">Picklists</span> (Admin) — Manage dropdown field options
            across all CRM entities. See{' '}
            <a
              href="#picklist-management"
              className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              details below
            </a>
            .
          </li>
          <li>
            <span className="font-medium">Renewals</span> (Admin) — Configure automatic renewal
            reminder tasks. See{' '}
            <a
              href="#renewal-templates"
              className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              details below
            </a>
            .
          </li>
          <li>
            <span className="font-medium">About</span> — Application version and build information
          </li>
        </ul>

        <ProTip>
          <span className="font-medium">Set up Google sync early:</span> Connecting your Google
          Calendar on day one means all your meetings automatically appear in the CRM — no double
          entry required.
        </ProTip>
      </section>
      <Separator />

      <section id="google-integration" aria-labelledby="google-integration-heading">
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="h-4 w-4" aria-hidden="true" />
          <h3 id="google-integration-heading" className="font-semibold">
            Google Integration (Gmail &amp; Calendar)
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Connecting Google is a <span className="font-medium">manual, per-user</span> step. Each
          person connects their <span className="font-medium">own</span> Google account — it is{' '}
          <span className="font-medium">not</span> turned on automatically when your account is
          created, and an administrator <span className="font-medium">cannot</span> connect it on
          your behalf. (Signing in with Google to log in is separate from connecting Gmail and
          Calendar sync — you still need to connect here.)
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">To connect your Google account:</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            Go to <span className="font-medium">Settings &rarr; Google</span>
          </li>
          <li>
            Click <span className="font-medium">Connect Google Account</span>
          </li>
          <li>
            Approve access on the Google consent screen (you must grant the requested Gmail and/or
            Calendar permissions)
          </li>
          <li>
            You are returned to the CRM and the status shows{' '}
            <span className="font-medium">Connected</span> with your Google email
          </li>
        </ol>
        <p className="text-sm text-muted-foreground mb-2">
          Until you connect, the tab shows <span className="font-medium">Not Connected</span> and
          the sync settings stay hidden — so Gmail and Calendar sync will not run for you. Use{' '}
          <span className="font-medium">Disconnect</span> to revoke access and stop syncing, or{' '}
          <span className="font-medium">Reconnect</span> if the connection expires (re-authorize
          with the same Google account).
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Super Admin sync options:</span> when a Super Admin
          connects, checkboxes let them exclude Gmail and/or Calendar sync at connect time. Regular
          users sync both Gmail and Calendar by default.
        </p>
        <p className="text-sm text-muted-foreground">
          Once connected, see{' '}
          <a
            href="#emails"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Activities &rarr; Emails
          </a>{' '}
          for email sync and{' '}
          <a
            href="#events"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Events
          </a>{' '}
          for calendar sync and Google Meet scheduling.
        </p>
        <ProTip>
          <span className="font-medium">Not seeing your emails or meetings?</span> The most common
          reason is simply that you have not connected your Google account yet. Open{' '}
          <span className="font-medium">Settings &rarr; Google</span> and check whether it says
          Connected.
        </ProTip>
      </section>
      <Separator />

      <section id="enhancement-tracker" aria-labelledby="enhancement-tracker-heading">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="h-4 w-4" aria-hidden="true" />
          <h3 id="enhancement-tracker-heading" className="font-semibold">
            Enhancement Tracker
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Have an idea to make Grackle CRM work better for your team? The Enhancement Tracker (Admin
          access required) lets you submit and track feature requests:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Submit requests with a title, description, and category</li>
          <li>Set priority — Low, Medium, High, or Critical</li>
          <li>Track status from New through In Progress, Completed, or Rejected</li>
          <li>Filter by status and priority to see what is in the queue</li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          If you are not an admin, share your ideas with your administrator and they can log them on
          your behalf.
        </p>
      </section>
      <Separator />

      <section id="mailchimp-integration" aria-labelledby="mailchimp-heading">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="h-4 w-4" aria-hidden="true" />
          <h3 id="mailchimp-heading" className="font-semibold">
            Mailchimp Integration
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Available to:</span> Admin, Super Admin, and Marketing users
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          Connect your Mailchimp account to sync CRM contacts to Mailchimp audiences for email
          marketing campaigns. The integration uses API key authentication and provides visibility
          between your CRM data and Mailchimp lists.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">To connect Mailchimp:</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            Go to <span className="font-medium">Settings &rarr; Mailchimp Integration</span>
          </li>
          <li>
            Find your API key in Mailchimp under{' '}
            <span className="font-medium">Account &rarr; Extras &rarr; API keys</span>
          </li>
          <li>
            Paste the API key and click <span className="font-medium">Connect Mailchimp</span>
          </li>
          <li>Your account name, datacenter, and audiences will appear</li>
        </ol>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Syncing contacts:</span> Once connected, sync CRM contacts
          to any Mailchimp audience:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Quick Sync</span> — Incremental sync of recently updated
            contacts
          </li>
          <li>
            <span className="font-medium">Full Sync</span> — Complete sync of all CRM contacts to
            the selected audience
          </li>
        </ul>
        <p className="text-sm text-muted-foreground">
          Sync progress is displayed in real-time. Click{' '}
          <span className="font-medium">Disconnect</span> to revoke access — this stops syncing but
          does not delete any data from Mailchimp.
        </p>
      </section>
      <Separator />

      <section id="notifications-settings" aria-labelledby="notifications-settings-heading">
        <div className="flex items-center gap-2 mb-2">
          <Bell className="h-4 w-4" aria-hidden="true" />
          <h3 id="notifications-settings-heading" className="font-semibold">
            Notifications
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          The CRM delivers real-time notifications to keep you informed of important events without
          refreshing the page. Notifications appear in the bell icon in the header, with an unread
          badge showing the count.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Notification types:</span>
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Assignment</span> — You have been assigned a task, lead,
            opportunity, or ticket
          </li>
          <li>
            <span className="font-medium">Closed Won</span> — An opportunity has closed successfully
          </li>
          <li>
            <span className="font-medium">SLA Breach</span> — A support ticket has breached its SLA
            threshold
          </li>
          <li>
            <span className="font-medium">Stage Change</span> — An opportunity stage has changed
          </li>
          <li>
            <span className="font-medium">Comment</span> — Someone commented on a ticket you are
            following
          </li>
          <li>
            <span className="font-medium">Due Tomorrow / Due Today / Overdue</span> — Automatic
            daily reminders for tasks approaching or past their due date
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Using notifications:</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Click the bell icon in the header to open the notification panel</li>
          <li>Click any notification to navigate to the related record</li>
          <li>
            Click <span className="font-medium">Mark all read</span> to clear all unread
            notifications
          </li>
        </ol>

        <ProTip>
          Notifications are delivered instantly via WebSocket. If you stop receiving them, refresh
          the page to reconnect.
        </ProTip>
      </section>
      <Separator />

      <section id="picklist-management" aria-labelledby="picklist-management-heading">
        <div className="flex items-center gap-2 mb-2">
          <Settings2 className="h-4 w-4" aria-hidden="true" />
          <h3 id="picklist-management-heading" className="font-semibold">
            Picklist Management
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Available to:</span> Admin and Super Admin users
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          Manage the dropdown field options (stages, statuses, priorities, categories, etc.) used
          across all CRM entities. Access this feature from{' '}
          <span className="font-medium">Settings &rarr; Picklists</span> tab.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">How it works:</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            Select an entity tab (Account, Contact, Lead, Opportunity, Task, Event, Quote, Order, or
            General)
          </li>
          <li>Choose a field from the dropdown to manage its values</li>
          <li>
            Add new values, edit existing ones inline, reorder with up/down arrows, toggle
            active/inactive, or set a default
          </li>
        </ol>
        <p className="text-sm text-muted-foreground">
          Inactive values are hidden from dropdowns but preserved on existing records. This lets you
          retire options without losing historical data.
        </p>
      </section>
      <Separator />

      <section id="renewal-templates" aria-labelledby="renewal-templates-heading">
        <div className="flex items-center gap-2 mb-2">
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          <h3 id="renewal-templates-heading" className="font-semibold">
            Renewal Task Templates
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Available to:</span> Admin and Super Admin users
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          Configure automatic renewal reminder tasks that are created when an opportunity closes to
          Closed Won. Access this feature from{' '}
          <span className="font-medium">Settings &rarr; Renewals</span> tab.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">How it works:</span> When an opportunity with a contract end
          date is marked Closed Won, the system automatically creates tasks at the configured
          intervals before the contract renewal date.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Default templates:</span> 90, 60, and 45 days before
          contract end.
        </p>
        <p className="text-sm text-muted-foreground mb-2">For each template you can configure:</p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Days before renewal to create the task</li>
          <li>Task subject line</li>
          <li>Priority level (High, Normal, Low)</li>
          <li>Description with instructions for the assignee</li>
        </ul>
      </section>
      <Separator />

      <section id="google-chat-settings" aria-labelledby="google-chat-settings-heading">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-4 w-4" aria-hidden="true" />
          <h3 id="google-chat-settings-heading" className="font-semibold">
            Google Chat Notifications
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Available to:</span> Admin and Super Admin users
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          Send CRM event notifications to a Google Chat space so your team stays informed without
          checking the CRM. Access this feature from{' '}
          <span className="font-medium">Settings &rarr; Google Chat</span> tab.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">To set up:</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            Create a webhook in your Google Chat space (Space settings &rarr; Integrations &rarr;
            Webhooks)
          </li>
          <li>
            Copy the webhook URL and paste it in{' '}
            <span className="font-medium">Settings &rarr; Google Chat</span>
          </li>
          <li>
            Select which events should trigger messages (lead created, opportunity stage changed,
            deal closed, task assigned, task due date reminders)
          </li>
          <li>Toggle the integration on</li>
        </ol>
        <p className="text-sm text-muted-foreground">
          Messages are sent in real-time as events occur. Disable individual event types or the
          entire integration at any time.
        </p>
      </section>
    </>
  )
}
